import React, { PropsWithChildren, useCallback, useState } from 'react';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { Icons, SVG } from '../../../components/atoms/SVG';
import { AcceptDeclineForm } from '../../../_lib/Form2';
import ModalDialog from '../../../components/organisms/ModalDialog/ModalDialog';
import { useDelayed } from '../../../hooks/useDelayed';
import { useInvalidateRefreshableBlock, useRefreshBlock } from '../../../_lib/BlockFetcher';
import { EnrichedAdditionalPrivilegeItem } from '../PrivilegeConfig.types';
import { BlockType } from '../../../types/api/ElementType';
import { useAppContext } from '../../../contexts/AppContext/AppContext';
import { BlockBar, BlockBarButton } from '../../../_lib/components/BlockBar';

type PrivilegeContainerProps = PropsWithChildren<{
  privilege: EnrichedAdditionalPrivilegeItem;
}>;

export function AdditionalPrivilegeContainer({ privilege, children }: PrivilegeContainerProps) {
  const { t } = useSonTranslation('PrivilegeConfig');
  const { active } = useAppContext().editModeInfo;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { refresh } = useRefreshBlock();
  const closeModal = useCallback(() => {
    setShowDeleteModal(false);
  }, []);
  const invalidateRefreshableBlock = useInvalidateRefreshableBlock();

  const refreshAndClose = useCallback(() => {
    refresh();
    // we also want to refresh the basic privileges block.
    invalidateRefreshableBlock(BlockType.basicprivilegeconfig);
    closeModal();
  }, [closeModal, refresh, invalidateRefreshableBlock]);
  const delayedRefreshAndClose = useDelayed(refreshAndClose);

  return (
    <div className="privilege-config__additional-privilege-container">
      <div className="privilege-config__related-icon">
        <SVG svgSpriteId={Icons.PAGE_TO_PAGE_ROLE_RELATED} />
      </div>
      <BlockBar heading={privilege.label} hierarchy={3} modifier="no-padding-bottom">
        {active
          ? <BlockBarButton
              text={t('actions.delete.headline', {
                name: privilege.label,
              })}
              svgSpriteId={Icons.DELETE_TRASHCAN}
              onClick={() => setShowDeleteModal(true)}
            />
          : null}
      </BlockBar>
      <div className="privilege-config__container">
        {children}
      </div>
      {showDeleteModal
        ? (
            <ModalDialog
              closeModal={() => setShowDeleteModal(false)}
              headline={t('actions.delete.headline', {
                name: privilege.label,
              })}
              initialFocusIndex={1}
            >
              <AcceptDeclineForm
                messageSuccess={t('actions.delete.status.success')}
                messageSending={t('actions.delete.status.sending')}
                messageFail={t('actions.delete.status.fail')}
                headline={t('actions.delete.question')}
                form={privilege.delete}
                onSuccess={delayedRefreshAndClose}
              />
            </ModalDialog>
          )
        : null}
    </div>
  );
}
